'use client';

import type { TicketGroup } from '@interfaces/ticketevolution/tikets';
import { useTicketsStore } from '@store/tickets-store';
import { useEffect, useMemo } from 'react';
import {
  boundTicketPriceRange,
  countActiveTicketFilters,
  getTicketFilterOptions,
} from './ticket-filters';
import { useFilteredSortedTickets } from './use-filtered-tickets';

export function useTicketFilterModel(
  ticketsList: TicketGroup[],
  { includeSortCount = false }: { includeSortCount?: boolean } = {}
) {
  const zoneBuckets = useTicketsStore((state) => state.zoneBuckets);
  const accessibleOnly = useTicketsStore((state) => state.accessibleOnly);
  const underPriceCap = useTicketsStore((state) => state.underPriceCap);
  const isOnlyElectronic = useTicketsStore((state) => state.isOnlyElectronic);
  const selectedGroups = useTicketsStore((state) => state.selectedGroups);
  const setSelectedGroups = useTicketsStore((state) => state.setSelectedGroups);
  const priceRange = useTicketsStore((state) => state.priceRange);
  const setPriceRange = useTicketsStore((state) => state.setPriceRange);
  const sortBy = useTicketsStore((state) => state.sortby);
  const resetFilters = useTicketsStore((state) => state.resetFilters);

  const options = useMemo(
    () => getTicketFilterOptions(ticketsList),
    [ticketsList]
  );

  useEffect(() => {
    const current = useTicketsStore.getState().priceRange;
    const next = boundTicketPriceRange(current, options.fullPriceRange);
    if (next[0] !== current[0] || next[1] !== current[1]) {
      setPriceRange(next);
    }
  }, [options.fullPriceRange.max, options.fullPriceRange.min, setPriceRange]);

  const hasPriceRangeFilter =
    priceRange[0] !== options.fullPriceRange.min ||
    priceRange[1] !== options.fullPriceRange.max;

  const activeCount = countActiveTicketFilters({
    zoneBuckets,
    accessibleOnly,
    underPriceCap,
    isOnlyElectronic,
    selectedGroups,
    hasPriceRangeFilter,
    sortIsDefault: sortBy === 'lowestPrice',
    includeSortCount,
  });

  const { resultCount } = useFilteredSortedTickets(ticketsList);

  const clearAll = () => {
    resetFilters();
    setSelectedGroups([]);
    setPriceRange([options.fullPriceRange.min, options.fullPriceRange.max]);
  };

  return {
    options,
    activeCount,
    hasActive: activeCount > 0,
    clearAll,
    resultCount,
  };
}
